import { useState, useEffect, useRef, useCallback } from 'react';
import { APIService } from '@/services/api';
import { APIRoutes } from '@/services/api.routes';
import type { DayTradingSessionState, DayTradingSessionChat } from '@/models/day-trading-session';
import type {DayTradingParticipant, DayTradingParticipantState} from '@/models/day-trading-participant';
import type { DayTradingSessionID } from '@/models/ids';
import type { ConnectionStatus } from '@/components/daytrading/components/ConnectionStatusIndicator';
import type {
  DayTradingWSIncomingMessage,
  DayTradingWSIncomingMessageType,
  DayTradingWSOutgoingMessage,
} from '@/models/day-trading-websocket';
import type {RFC3339, User} from "@/models";
import type { StreamMode } from '@/models/enums';

export interface DayTradingStreamInfo {
  mode: StreamMode;
  youtubeURL: string;
  googleMeetURL: string;
  discordURL: string;
  directStreamKey: string;
  directStreamIngestURL: string;
  directStreamViewerURL: string;
}

const emptyStreamInfo: DayTradingStreamInfo = {
  mode: 'NONE',
  youtubeURL: '',
  googleMeetURL: '',
  discordURL: '',
  directStreamKey: '',
  directStreamIngestURL: '',
  directStreamViewerURL: '',
};

type WSRole = 'moderator' | 'participant' | 'observer';

interface UseDayTradingWebSocketOptions {
  sessionId: DayTradingSessionID;
  role: WSRole;
}

interface UseDayTradingWebSocketReturn {
  initialLoading: boolean;
  initialError: string | null;
  connectionStatus: ConnectionStatus;
  error: string | null;
  sessionDeleted: boolean;

  sessionState: DayTradingSessionState | null;
  setSessionState: React.Dispatch<React.SetStateAction<DayTradingSessionState | null>>;

  sessionClosed: boolean;
  setSessionClosed: React.Dispatch<React.SetStateAction<boolean>>;

  participantState: DayTradingParticipantState | null;
  setParticipantState: React.Dispatch<React.SetStateAction<DayTradingParticipantState | null>>;

  participants: DayTradingParticipant[];
  setParticipants: React.Dispatch<React.SetStateAction<DayTradingParticipant[]>>;

  moderators: User[];
  setModerators: React.Dispatch<React.SetStateAction<User[]>>;

  moderatorPings: { [userID: string]: RFC3339 };
  setModeratorPings: React.Dispatch<React.SetStateAction<{ [userID: string]: RFC3339 }>>;

  moderatorPolls: { [userID: string]: RFC3339 };
  setModeratorPolls: React.Dispatch<React.SetStateAction<{ [userID: string]: RFC3339 }>>;

  streamInfo: DayTradingStreamInfo;
  setStreamInfo: React.Dispatch<React.SetStateAction<DayTradingStreamInfo>>;

  chat: DayTradingSessionChat | null;
  setChat: React.Dispatch<React.SetStateAction<DayTradingSessionChat | null>>;
}

export function streamInfoFromSession(session: { streamMode: StreamMode; youtubeURL: string; googleMeetURL: string; discordURL: string; directStreamKey: string; directStreamIngestURL: string; directStreamViewerURL: string; }): DayTradingStreamInfo {
  return {
    mode: session.streamMode,
    youtubeURL: session.youtubeURL,
    googleMeetURL: session.googleMeetURL,
    discordURL: session.discordURL,
    directStreamKey: session.directStreamKey,
    directStreamIngestURL: session.directStreamIngestURL,
    directStreamViewerURL: session.directStreamViewerURL,
  };
}

function buildWSUrl(sessionId: string, role: WSRole): string | null {
  const token = APIService.inst.getToken();
  if (token === null) return null;

  const apiUrl = import.meta.env.VITE_API_URL as string;
  const wsUrl = apiUrl.replace(/^http/, 'ws');
  const endpointMap: Record<WSRole, string> = {
    moderator: 'ws-moderator',
    participant: 'ws-participant',
    observer: 'ws-observe',
  };
  return `${wsUrl}/day-trading/sessions/${sessionId}/${endpointMap[role]}?auth=${encodeURIComponent(token)}`;
}

function wsLog(v: string) {
  const now = new Date();
  console.log(`Websocket [${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}]: ${v}`);
}

let refCounter = 0;
function nextRef(): string {
  refCounter++;
  return `ref-${Date.now()}-${refCounter}`;
}

export function useDayTradingWebSocket({ sessionId, role }: UseDayTradingWebSocketOptions): UseDayTradingWebSocketReturn {
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialError, setInitialError] = useState<string | null>(null);
  const [sessionState, setSessionState] = useState<DayTradingSessionState | null>(null);
  const [participants, setParticipants] = useState<DayTradingParticipant[]>([]);
  const [moderators, setModerators] = useState<User[]>([]);
  const [moderatorPings, setModeratorPings] = useState<{ [userID: string]: RFC3339 }>({});
  const [moderatorPolls, setModeratorPolls] = useState<{ [userID: string]: RFC3339 }>({});
  const [participantState, setParticipantState] = useState<DayTradingParticipantState | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [sessionDeleted, setSessionDeleted] = useState(false);
  const [streamInfo, setStreamInfo] = useState<DayTradingStreamInfo>(emptyStreamInfo);
  const [chat, setChat] = useState<DayTradingSessionChat | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const bgReconnectIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const failureCountRef = useRef(0);
  const mountedRef = useRef(true);
  const sessionClosedRef = useRef(false);
  const sessionDeletedRef = useRef(false);
  const initialFetchDoneRef = useRef(false);

  // Keep refs in sync with state
  sessionClosedRef.current = sessionClosed;
  sessionDeletedRef.current = sessionDeleted;

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current !== null) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current !== null) return;

    wsLog('setting mode to [polling]');

    setConnectionStatus('polling');

    const poll = async () => {
      if (!mountedRef.current) return;
      try {
        const isModerating = role === 'moderator' || role === 'observer';
        
        const session = await APIRoutes.getDayTradingSession(sessionId, (role === 'moderator'), (role === 'participant'));
        if (!mountedRef.current) return;

        setSessionState(session.state);
        setSessionClosed(session.end !== null);
        setStreamInfo(streamInfoFromSession(session));
        setChat(session.chat);

        if (isModerating) {
          setParticipants(session.participants ?? []);
          setModerators(session.moderators);
          setModeratorPings(session.moderatorPings);
          setModeratorPolls(session.moderatorPolls);
        }

        if (role === 'participant') {
          try {
            const participant = await APIRoutes.getDayTradingParticipant(sessionId, 'self');
            if (!mountedRef.current) return;
            setParticipantState(participant.state);
          } catch {
            // Participant fetch may fail if not joined yet
          }
        }
      } catch {
        // Polling failure is non-critical
      }
    };

    poll().then();
    pollingIntervalRef.current = setInterval(poll, 5*1000);
  }, [sessionId, role]);

  // Use refs for connect/scheduleReconnect to break circular dependency
  const connectRef = useRef<() => void>(() => {});
  const scheduleReconnectRef = useRef<() => void>(() => {});

  connectRef.current = () => {
    wsLog('connectref.current() called');

    if (!mountedRef.current) return;
    if (sessionClosedRef.current || sessionDeletedRef.current) return;

    const url = buildWSUrl(sessionId, role);
    if (url === null) {
      setError('Nicht authentifiziert');
      setConnectionStatus('disconnected');
      return;
    }

    if (connectionStatus === 'disconnected') setConnectionStatus('connecting');

    const ws = new WebSocket(url);
    wsRef.current = ws;

    // Force-close if connection is not established within 5 seconds.
    // Browsers may delay the onclose event for up to 60s even when the
    // connection is refused immediately at the network level.
    const connectTimeout = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        wsLog('close by connect-timeout');
        ws.close();
      }
    }, 5000);

    ws.onopen = () => {
      wsLog('onopen');
      clearTimeout(connectTimeout);
      if (!mountedRef.current) return;
      failureCountRef.current = 0;
      setConnectionStatus('connected');
      setError(null);
      stopPolling();

      if (bgReconnectIntervalRef.current !== null) {
        clearInterval(bgReconnectIntervalRef.current);
        bgReconnectIntervalRef.current = null;
      }

      const queryTypeMap: Record<WSRole, DayTradingWSIncomingMessageType> = {
        moderator: 'MODERATOR::QUERY_STATE',
        participant: 'PARTICIPANT::QUERY_STATE',
        observer: 'MODERATOR::QUERY_STATE',
      };
      const msg: DayTradingWSIncomingMessage = { type: queryTypeMap[role], reference: nextRef() };
      ws.send(JSON.stringify(msg));
    };

    ws.onmessage = (event) => {
      if (!mountedRef.current) return;
      try {
        const msg = JSON.parse(event.data as string) as DayTradingWSOutgoingMessage;

        switch (msg.type) {
          case 'ANY::PING': {
            const pong: DayTradingWSIncomingMessage = { type: 'ANY::PING_RESPONSE', reference: msg.reference };
            ws.send(JSON.stringify(pong));
            break;
          }
          case 'ANY::ERROR':
            setError(msg.error ?? 'Unbekannter Fehler');
            break;
          case 'ANY::SESSION_CLOSED':
            setSessionClosed(true);
            break;
          case 'ANY::SESSION_DELETED':
            setSessionDeleted(true);
            break;
          case 'MODERATOR::STATE':
            if (msg.sessionState !== undefined) setSessionState(msg.sessionState);
            if (msg.session !== undefined) setParticipants(msg.session.participants ?? []);
            if (msg.session !== undefined) setModerators(msg.session.moderators);
            if (msg.session !== undefined) setModeratorPings(msg.session.moderatorPings);
            if (msg.session !== undefined) setModeratorPolls(msg.session.moderatorPolls);
            if (msg.session !== undefined) setStreamInfo(streamInfoFromSession(msg.session));
            if (msg.session !== undefined) setChat(msg.session.chat);
            if (msg.chat !== undefined) setChat(msg.chat);
            if (msg.participantState !== undefined) setParticipantState(msg.participantState);
            break;
          case 'PARTICIPANT::STATE':
            if (msg.sessionState !== undefined) setSessionState(msg.sessionState);
            if (msg.session !== undefined) setStreamInfo(streamInfoFromSession(msg.session));
            if (msg.session !== undefined) setChat(msg.session.chat);
            if (msg.chat !== undefined) setChat(msg.chat);
            if (msg.participantState !== undefined) setParticipantState(msg.participantState);
            break;
        }
      } catch {
        // Ignore malformed messages
      }
    };

    ws.onclose = () => {
      wsLog('onclose');
      clearTimeout(connectTimeout);
      if (!mountedRef.current) return;
      wsRef.current = null;
      scheduleReconnectRef.current();
    };

    ws.onerror = () => {
      wsLog('onerror');
      // onclose will fire after onerror
    };
  };

  scheduleReconnectRef.current = () => {
    if (!mountedRef.current) return;
    if (sessionClosedRef.current || sessionDeletedRef.current) {
      setConnectionStatus('disconnected');
      return;
    }

    failureCountRef.current++;
    const failures = failureCountRef.current;

    wsLog('scheduleReconnectRef called with failureCount := ' + failures);

    if (failures >= 3) {
      startPolling();

      if (bgReconnectIntervalRef.current === null) {
        bgReconnectIntervalRef.current = setInterval(() => {
          if (mountedRef.current && wsRef.current === null) {
            wsLog('re-connect by [bgReconnectIntervalRef]');
            connectRef.current();
          }
        }, 10000);
      }
      return;
    }

    setConnectionStatus('reconnecting');
    const delay = Math.min(1000 * Math.pow(2, failures - 1), 30000);
    reconnectTimeoutRef.current = setTimeout(() => {
      wsLog('re-connect by [reconnectTimeoutRef]');
      reconnectTimeoutRef.current = null;
      connectRef.current();
    }, delay);
  };

  // Initial fetch + connection
  useEffect(() => {
    let active = true; // scoped to THIS effect invocation (prevents double-connect in StrictMode)
    mountedRef.current = true;
    initialFetchDoneRef.current = false;
    failureCountRef.current = 0;

    const initialFetch = async () => {
      try {
        const session = await APIRoutes.getDayTradingSession(sessionId);
        if (!active) return;

        setSessionState(session.state);
        setSessionClosed(session.end !== null);
        setStreamInfo(streamInfoFromSession(session));
        setChat(session.chat);

        if (role === 'moderator' || role === 'observer') {
          setParticipants(session.participants ?? []);
          setModerators(session.moderators);
          setModeratorPings(session.moderatorPings);
          setModeratorPolls(session.moderatorPolls);
          if (role === 'moderator' && session.participant !== null) {
            setParticipantState(session.participant.state);
          }
        }

        if (role === 'participant' && session.participant !== null) {
          setParticipantState(session.participant.state);
        }

        initialFetchDoneRef.current = true;
        setInitialLoading(false);
        setInitialError(null);

        wsLog('connectRef.current() called by initial useEffect');
        connectRef.current();
      } catch (err) {
        if (!active) return;
        const message = err instanceof Error ? err.message : 'Fehler beim Laden der Session';
        setInitialError(message);
        setInitialLoading(false);
      }
    };

    initialFetch().then();

    return () => {
      active = false;
      mountedRef.current = false;

      if (wsRef.current !== null) {
        wsLog('.close in initial useEffect');

        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }

      if (reconnectTimeoutRef.current !== null) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (pollingIntervalRef.current !== null) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      if (bgReconnectIntervalRef.current !== null) {
        clearInterval(bgReconnectIntervalRef.current);
        bgReconnectIntervalRef.current = null;
      }
    };
  }, [sessionId, role, stopPolling, startPolling]);

  return {
    initialLoading,
    initialError,
    connectionStatus,
    error,
    sessionDeleted,

    sessionState,
    setSessionState,

    sessionClosed,
    setSessionClosed,

    participantState,
    setParticipantState,

    participants,
    setParticipants,

    moderators,
    setModerators,

    moderatorPings,
    setModeratorPings,

    moderatorPolls,
    setModeratorPolls,

    streamInfo,
    setStreamInfo,

    chat,
    setChat,
  };
}
